/* STKT: functional request IDs and confirmed-response hooks on all native contact forms. */
(() => {
  const emitInteraction = (type, form) => {
    if (window.Shopify?.designMode) return;
    // Deliberately no form values, campaign identifiers, or external tags.
    const detail = form ? { type, form } : { type };
    document.dispatchEvent(new CustomEvent('mw-stkt:interaction', { detail }));
  };

  const formSelector = '.mw-stkt-hero__form, .mw-stkt-contact__form, .contact-form__form';
  const pendingKey = 'mw-stkt-pending-contact-v1';
  const pendingLifetime = 30 * 60 * 1000;
  const requestIdPattern = /^STKT-(?:[A-F0-9]{32}|[A-F0-9]{8}-[A-F0-9]{4}-[A-F0-9]{4}-[A-F0-9]{4}-[A-F0-9]{12})$/;
  const attempts = new WeakMap();
  const resetTimers = new WeakMap();

  const resetFormButton = (form) => {
    const button = form.querySelector('button[type="submit"]');
    if (button) {
      button.disabled = false;
      button.removeAttribute('aria-busy');
    }
  };

  const createRequestId = () => {
    try {
      if (window.crypto?.randomUUID) {
        return `STKT-${window.crypto.randomUUID().toUpperCase()}`;
      }
      if (window.crypto?.getRandomValues) {
        const bytes = window.crypto.getRandomValues(new Uint8Array(16));
        return `STKT-${Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('').toUpperCase()}`;
      }
    } catch (_) {
      // Contact delivery must still work if secure randomness is unavailable.
    }
    return null;
  };

  const removePending = () => {
    try {
      window.sessionStorage.removeItem(pendingKey);
      return true;
    } catch (_) {
      return false;
    }
  };

  const readPending = () => {
    try {
      const pending = JSON.parse(window.sessionStorage.getItem(pendingKey));
      if (!pending) return null;
      const age = Date.now() - pending.createdAt;
      if (
        !requestIdPattern.test(pending.requestId || '') ||
        !['hero', 'contact', 'standard'].includes(pending.source) ||
        typeof pending.formId !== 'string' || !pending.formId ||
        !Number.isFinite(pending.createdAt) || age < 0 || age > pendingLifetime
      ) {
        removePending();
        return null;
      }
      return pending;
    } catch (_) {
      removePending();
      return null;
    }
  };

  document.addEventListener('submit', (event) => {
    const form = event.target.closest(formSelector);
    if (!form) return;

    const source = form.querySelector('[data-mw-stkt-form-source]')?.dataset.mwStktFormSource;
    if (!['hero', 'contact', 'standard'].includes(source)) return;

    let attempt = attempts.get(form);
    const isNewAttempt = !attempt;
    if (!attempt) {
      attempt = { requestId: createRequestId(), formId: form.id, source, createdAt: Date.now() };
      attempts.set(form, attempt);
    }

    const requestField = form.querySelector('[data-mw-stkt-request-id]');
    if (requestField && attempt.requestId) {
      requestField.value = attempt.requestId;
      requestField.disabled = false;
    }

    if (attempt.requestId) {
      try {
        // Short-lived form-processing state only; no advertising attribution.
        window.sessionStorage.setItem(pendingKey, JSON.stringify(attempt));
      } catch (_) {
        removePending();
        // The email still contains its request ID; no confirmed event is emitted
        // after the response when storage is unavailable.
      }
    }

    const submitButton = form.querySelector('button[type="submit"]');
    if (submitButton) {
      submitButton.disabled = true;
      submitButton.setAttribute('aria-busy', 'true');
    }

    // Shopify may re-submit after a challenge. Keep the same request ID and
    // never prevent Shopify's native submission or count that attempt twice.
    if (isNewAttempt) emitInteraction('lead_form_submit', source);
    window.clearTimeout(resetTimers.get(form));
    resetTimers.set(form, window.setTimeout(() => resetFormButton(form), 15000));
  });

  const pending = readPending();
  if (pending) {
    const matchingSuccess = Array.from(document.querySelectorAll('[data-mw-stkt-form-success]')).find((message) => {
      const form = message.closest(formSelector);
      return form?.id === pending.formId && message.dataset.mwStktFormSuccess === pending.source;
    });
    if (matchingSuccess) {
      // Consume before dispatch: two forms, refreshes, and back/forward restores
      // must not turn one Shopify response into multiple success events.
      if (removePending()) emitInteraction('lead_form_success', pending.source);
    } else if (document.querySelector('.mw-stkt-hero__form-errors, .mw-stkt-contact__errors, .contact-form__error')) {
      removePending();
    }
  }

  window.addEventListener('pageshow', () => {
    document.querySelectorAll(formSelector).forEach((form) => {
      window.clearTimeout(resetTimers.get(form));
      resetFormButton(form);
      attempts.delete(form);
      const requestField = form.querySelector('[data-mw-stkt-request-id]');
      if (requestField) {
        requestField.value = '';
        requestField.disabled = true;
      }
    });
  });
})();
